const SWITCH_KEYS = ['VOL', 'LTL', 'smallPack', 'AIR', 'expedite']

export const quoteSwitchesPresent = (values) =>
  SWITCH_KEYS.some(key => typeof values?.[key] === 'boolean')

export const getSelectedVendorTypes = (values) => {
  const types = new Set()

  if (values?.VOL) types.add('VOL')
  if (values?.LTL) {
    types.add('LTL')
    types.add('3PL')
  }
  if (values?.smallPack) types.add('SP')
  if (values?.AIR) {
    types.add('AIR')
    types.add('INTL')
  }
  if (values?.expedite) types.add('GRE')

  return types
}

const isVolumeMode = (values) => values?.mode?.id === 0 || values?.mode?.shipmentCategory === 'Volume'

const listIncludesDrayage = (enabledVendorsFiltered) =>
  enabledVendorsFiltered?.some(vendor =>
    vendor?.type === 'DRAY' ||
    vendor?.carrierType === 'DRAY' ||
    vendor?.subVendors?.some(subVendor => subVendor?.carrierType === 'DRAY')
  )

const collectSubVendors = (vendor, carrierType, alreadyIncluded) => {
  const matched = []

  vendor?.subVendors?.filter(subVendor => subVendor?.carrierType === carrierType)?.forEach(subVendor => {
    if (!alreadyIncluded.includes(subVendor.carrierCode)) {
      matched.push(subVendor)
    }
  })

  return matched
}

const expandLegacy = (values, enabledVendorsFiltered, allEnabledVendors) => {
  const enabledVendorsFilteredLocal = []
  const volumeMode = isVolumeMode(values)

  enabledVendorsFiltered.forEach(vendor => {
    if (vendor?.subVendors?.length) {
      let subVendors = vendor.subVendors.filter(subVendor => subVendor?.carrierType === vendor?.type)

      if (vendor?.type !== 'LTL' && (values.LTL || volumeMode)) {
        subVendors.push(...vendor.subVendors.filter(subVendor => subVendor?.carrierType === 'LTL'))
      }

      if (vendor?.type !== '3PL' && (values.LTL || volumeMode)) {
        subVendors.push(...vendor.subVendors.filter(subVendor => subVendor?.carrierType === '3PL'))
      }

      if (vendor?.type !== 'VOL' && (values.VOL || volumeMode)) {
        subVendors.push(...vendor.subVendors.filter(subVendor => subVendor?.carrierType === 'VOL'))
      }

      if (vendor?.type !== 'GRE' && (values.expedite || volumeMode)) {
        subVendors.push(...vendor.subVendors.filter(subVendor => subVendor?.carrierType === 'GRE'))
      }

      if (vendor?.type !== 'SP' && (values.smallPack || values.mode?.shipmentCategory === 'Small Pack')) {
        subVendors.push(...vendor.subVendors.filter(subVendor => subVendor?.carrierType === 'SP'))
      }

      if (vendor?.type !== 'INTL' && (values.AIR || values.mode?.shipmentCategory === 'Air')) {
        subVendors.push(...vendor.subVendors.filter(subVendor => subVendor?.carrierType === 'INTL'))
      }

      if (vendor?.type !== 'AIR' && (values.AIR || values.mode?.shipmentCategory === 'Air')) {
        subVendors.push(...vendor.subVendors.filter(subVendor => subVendor?.carrierType === 'AIR'))
      }

      subVendors?.forEach(subVendor => {
        enabledVendorsFilteredLocal.push({...subVendor, id: vendor.id})
      })
    } else {
      enabledVendorsFilteredLocal.push(vendor)
    }
  })

  allEnabledVendors?.forEach(vendor => {
    let subVendors = []
    const enabledVendorsCarrierCodes = enabledVendorsFilteredLocal.map(entry => entry.carrierCode)

    if (vendor?.type !== 'LTL' && (values.LTL || volumeMode)) {
      subVendors.push(...collectSubVendors(vendor, 'LTL', enabledVendorsCarrierCodes))
    }

    if (vendor?.type !== '3PL' && (values.LTL || volumeMode)) {
      subVendors.push(...collectSubVendors(vendor, '3PL', enabledVendorsCarrierCodes))
    }

    if (vendor?.type !== 'VOL' && (values.VOL || volumeMode)) {
      subVendors.push(...collectSubVendors(vendor, 'VOL', enabledVendorsCarrierCodes))
    }

    if (vendor?.type !== 'GRE' && (values.expedite || volumeMode)) {
      subVendors.push(...collectSubVendors(vendor, 'GRE', enabledVendorsCarrierCodes))
    }

    if (vendor?.type !== 'SP' && (values.smallPack || values.mode?.shipmentCategory === 'Small Pack')) {
      subVendors.push(...collectSubVendors(vendor, 'SP', enabledVendorsCarrierCodes))
    }

    if (vendor?.type !== 'INTL' && (values.AIR || values.mode?.shipmentCategory === 'Air')) {
      subVendors.push(...collectSubVendors(vendor, 'INTL', enabledVendorsCarrierCodes))
    }

    if (vendor?.type !== 'AIR' && (values.AIR || values.mode?.shipmentCategory === 'Air')) {
      subVendors.push(...collectSubVendors(vendor, 'AIR', enabledVendorsCarrierCodes))
    }

    if (vendor?.type !== 'DRAY') {
      subVendors.push(...collectSubVendors(vendor, 'DRAY', enabledVendorsCarrierCodes))
    }

    subVendors.forEach(subVendor => {
      enabledVendorsFilteredLocal.push({...subVendor, id: vendor.id})
    })
  })

  return enabledVendorsFilteredLocal
}

const expandBySwitches = (values, enabledVendorsFiltered, allEnabledVendors) => {
  const allowedTypes = getSelectedVendorTypes(values)
  const includeDrayage = listIncludesDrayage(enabledVendorsFiltered)
  const enabledVendorsFilteredLocal = []

  const isAllowed = (carrierType) => allowedTypes.has(carrierType)

  enabledVendorsFiltered.forEach(vendor => {
    if (vendor?.subVendors?.length) {
      vendor.subVendors.filter(subVendor => {
        const carrierType = subVendor?.carrierType
        if (isAllowed(carrierType)) return true
        return includeDrayage && carrierType === 'DRAY' && vendor?.type === 'DRAY'
      }).forEach(subVendor => {
        enabledVendorsFilteredLocal.push({...subVendor, id: vendor.id})
      })
    } else if (
      isAllowed(vendor?.type) ||
      isAllowed(vendor?.carrierType) ||
      (includeDrayage && (vendor?.type === 'DRAY' || vendor?.carrierType === 'DRAY'))
    ) {
      enabledVendorsFilteredLocal.push(vendor)
    }
  })

  allEnabledVendors?.forEach(vendor => {
    const enabledVendorsCarrierCodes = enabledVendorsFilteredLocal.map(entry => entry.carrierCode)
    const subVendors = []

    vendor?.subVendors?.forEach(subVendor => {
      const carrierType = subVendor?.carrierType
      const allowed = isAllowed(carrierType) || (includeDrayage && carrierType === 'DRAY')
      if (!allowed || vendor?.type === carrierType) return
      if (!enabledVendorsCarrierCodes.includes(subVendor.carrierCode)) {
        subVendors.push(subVendor)
      }
    })

    subVendors.forEach(subVendor => {
      enabledVendorsFilteredLocal.push({...subVendor, id: vendor.id})
    })
  })

  return enabledVendorsFilteredLocal
}

export const expandEnabledVendors = (values, enabledVendorsFiltered, allEnabledVendors) => {
  if (!quoteSwitchesPresent(values)) {
    return expandLegacy(values, enabledVendorsFiltered, allEnabledVendors)
  }

  return expandBySwitches(values, enabledVendorsFiltered, allEnabledVendors)
}
